import { ExportOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  getDefaultSortOrder,
  Input,
  Select,
  Table,
  TextField,
} from "@pankod/refine-antd";
import { getAccounting } from "apis/accounting/getApiAccounting";
import { useSelectTime } from "hooks/useSelectTime";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import routerProvider from "@pankod/refine-react-router-v6";
import moment from "moment";
import { CSVLink } from "react-csv";
import "./styles.scss";

export const PayoutsOverviewListPrintOut = ({
  labelId,
  tabIndex,
  labelName,
}: any) => {
  const { Option } = Select;
  const [filter, setFilter] = useState("");
  const timeoutRef = useRef<any>(null);
  const { startTime, endTime, onChangeFilter, timeFramePayoutOptions } =
    useSelectTime();

  const { Link } = routerProvider;

  const [loading, setLoading] = React.useState(Boolean);
  const [contributors, setContributors] = useState<any>(null);
  const [contributorsOriginal, setContributorsOriginal] = useState<any>(null);

  const handleData = useCallback(async () => {
    const data = await getAccounting(
      {
        // search: filter.search,
        startTime: startTime.startTime,
        endTime: endTime.endTime,
        labelId,
        isAlreadyPayout: true,
      },
      "accounting"
    );
    const customData = data?.data?.data.map((i: any) => ({
      ...i,
      paypalEmailExport: i?.metQuota ? i?.contributor?.paypalEmail : "NO QUOTA",
      currencyExport: i?.metQuota ? "USD" : "NO QUOTA",
      contributorNameExport: i?.contributor?.username || "",
      amountExport: i?.metQuota
        ? +i?.trackEarnings + +i?.releaseEarnings +  Number(
                      i?.numberSubscriptions *
                        (i?.perDownloadValue || 0)
                    ) 
        : "NO QUOTA",
      accountingPeriodExport: `${moment(i?.startDate).format("L")} - ${moment(
        i?.endDate
      )
        .subtract(1, "days")
        .format("L")}`,
    }));
    setContributors(customData || []);
    setContributorsOriginal(customData || []);
  }, [endTime.endTime, labelId, startTime.startTime]);

  useEffect(() => {
    setLoading(contributors === null);
  }, [contributors]);

  useEffect(() => {
    if (tabIndex === "3") {
      handleData();
    }
  }, [handleData, tabIndex]);

  const handleChangeSearchStep1 = useCallback(
    (e) => {
      setLoading(true);
      const value = e.target.value;
      setFilter(value);
      if (timeoutRef) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        const filters = contributorsOriginal.filter(
          (i: any) =>
            i?.contributorNameExport
              ?.toLowerCase()
              .includes(value?.toLowerCase()) ||
            i?.paypalEmailExport?.toLowerCase().includes(value.toLowerCase())
        );
        setContributors(filters);
        setLoading(true);
      }, 500);
    },
    [contributorsOriginal]
  );

  const dataTable = useMemo(() => {
    return {
      dataSource: contributors,
      loading: loading,
    };
  }, [contributors, loading]);

  const handleTimeChange = useCallback(
    (value: any) => {
      setFilter("");
      setLoading(true);
      onChangeFilter("timeFrame", value);
    },
    [onChangeFilter]
  );

  const headers = [
    { label: "Contributor Name", key: "contributorNameExport" },
    { label: "Paypal Email", key: "paypalEmailExport" },
    { label: "Payout amount($)", key: "amountExport" },
    { label: "Currency", key: "currencyExport" },
    { label: "Accounting Period", key: "accountingPeriodExport" },
  ];

  const csvReport = {
    data: dataTable?.dataSource || [],
    headers: headers,
    filename: `AccoutingPrintout_${labelName}_${moment
      .unix(startTime.startTime)
      .format("MM/DD/YYYY")}-${moment
      .unix(endTime.endTime)
      .subtract(1, "days")
      .format("MM/DD/YYYY")}.csv`,
  };

  return (
    <div>
      <div className="flex">
        <Form.Item
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            defaultValue={timeFramePayoutOptions[1]}
            style={{
              width: 250,
            }}
            onChange={handleTimeChange}
          >
            {timeFramePayoutOptions.map((province) => (
              <Option key={province.value}>{province.label}</Option>
            ))}
          </Select>
        </Form.Item>
        <div>
          <CSVLink {...csvReport}>
            <Button disabled={loading}>
              <ExportOutlined />
              Export
            </Button>
            <CSVLink {...csvReport}></CSVLink>
          </CSVLink>
        </div>
      </div>

      <div>
        <div
          style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap" }}
        >
          <Input
            onChange={handleChangeSearchStep1}
            value={filter}
            placeholder="Search Contributor Name, Paypal Email"
          />
        </div>
        <div>
          <Table
            {...dataTable}
            pagination={false}
            scroll={{ x: true }}
            rowKey="id"
          >
            <Table.Column
              dataIndex="contributor"
              key="contributor"
              title="Contributor Name"
              sorter={(a: any, b: any) =>
                a.contributor.username.localeCompare(b.contributor.username)
              }
              render={(value) => (
                <Link target="_blank" to={`/users/edit/${value?._id}`}>
                  {value.username}
                </Link>
              )}
              defaultSortOrder={getDefaultSortOrder("contributor")}
            />
            <Table.Column
              dataIndex="paypalEmailExport"
              key="paypalEmailExport"
              title="Paypal Email"
              sorter={(a: any, b: any) =>
                a.paypalEmailExport.localeCompare(b.paypalEmailExport)
              }
              render={(_, record: any) => (
                <TextField
                  className={record?.metQuota ? "unset" : "noQuota"}
                  value={record?.paypalEmailExport}
                />
              )}
              defaultSortOrder={getDefaultSortOrder("contributor")}
            />
            <Table.Column
              dataIndex="amountExport"
              key="amountExport"
              title="Payout amount($)"
              sorter={(a: any, b: any) => a.amountExport - b.amountExport}
              render={(_, record: any) => (
                <TextField
                  className={record?.metQuota ? "unset" : "noQuota"}
                  value={record?.amountExport}
                />
              )}
              defaultSortOrder={getDefaultSortOrder("yourCut")}
            />
            <Table.Column
              dataIndex="metQuota"
              key="metQuota"
              title="Currency"
              render={(value) => (
                <TextField
                  className={value ? "unset" : "noQuota"}
                  value={value ? "USD" : "NO QUOTA"}
                />
              )}
            />
            <Table.Column
              dataIndex="time"
              key="time"
              title="Accounting Period"
              render={(_, record: any) => (
                <TextField
                  value={`${moment(record?.startDate).format("L")} - ${moment(
                    record?.endDate
                  )
                    .subtract(1, "days")
                    .format("L")}`}
                />
              )}
            />
          </Table>
        </div>
      </div>
    </div>
  );
};
